import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { matchPath, Outlet, useLocation } from 'react-router-dom';
import { SettingsModal } from '@/components/SettingsModal';
import Sidebar from '@/components/Sidebar';
import { MOBILE_LAYOUT_MEDIA_QUERY } from '@/const/layout';
import { useMediaQuery } from '@/helpers/hooks';
import { RoutePath } from '@/modules/CustomRouter/type';
import styles from './WorkspaceLayout.module.less';
/**
 * Workspace 布局：左侧 Sidebar + 右侧内容区。
 * 路由切换时父布局复用，Sidebar 不重渲染。
 */ export default function WorkspaceLayout(props) {
    const { defaultSidebarCollapsed = false } = props;
    const { pathname } = useLocation();
    const isProjectRoute = Boolean(matchPath(RoutePath.WorkspaceProjects, pathname) || matchPath(RoutePath.WorkspaceProjectDetail, pathname));
    const isWorkspaceRootRoute = pathname === RoutePath.Workspace;
    const isWorkspaceProjectsRoute = pathname === RoutePath.WorkspaceProjects;
    const isWorkspaceTrashRoute = pathname === RoutePath.WorkspaceTrash;
    const isMobileLayout = useMediaQuery(MOBILE_LAYOUT_MEDIA_QUERY);
    const [sidebarVisible, setSidebarVisible] = useState(false);
    const shouldUseOverlaySidebar = isProjectRoute || isMobileLayout && (isWorkspaceRootRoute || isWorkspaceProjectsRoute || isWorkspaceTrashRoute);
    useEffect(()=>{
        setSidebarVisible(false);
    }, [
        pathname
    ]);
    useEffect(()=>{
        if (!sidebarVisible) {
            return;
        }
        const handleKeyDown = (event)=>{
            if (event.key === 'Escape') {
                setSidebarVisible(false);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return ()=>{
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [
        sidebarVisible
    ]);
    const openWorkspaceSidebar = useCallback(()=>{
        if (shouldUseOverlaySidebar) {
            setSidebarVisible(true);
        }
    }, [
        shouldUseOverlaySidebar
    ]);
    const closeWorkspaceSidebar = useCallback(()=>{
        setSidebarVisible(false);
    }, []);
    const outletContext = useMemo(()=>({
            openWorkspaceSidebar,
            closeWorkspaceSidebar,
            shouldUseOverlaySidebar
        }), [
        closeWorkspaceSidebar,
        openWorkspaceSidebar,
        shouldUseOverlaySidebar
    ]);
    const overlayVisible = shouldUseOverlaySidebar && sidebarVisible;
    const shouldShowSidebarMask = overlayVisible;
    return /*#__PURE__*/ _jsxs("div", {
        className: classNames(styles.layout, {
            [styles['layout-project-route']]: isProjectRoute
        }),
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: classNames(styles['sidebar-shell'], {
                    [styles['sidebar-shell-overlay-route']]: shouldUseOverlaySidebar,
                    [styles['sidebar-shell-overlay-visible']]: overlayVisible
                }),
                children: /*#__PURE__*/ _jsx(Sidebar, {
                    defaultCollapsed: defaultSidebarCollapsed,
                    projectOverlayMode: shouldUseOverlaySidebar,
                    projectOverlayVisible: overlayVisible,
                    disableNavItemMotion: shouldUseOverlaySidebar,
                    onProjectOverlayClose: closeWorkspaceSidebar
                })
            }),
            /*#__PURE__*/ _jsx("main", {
                className: classNames(styles.content, {
                    [styles['content-project-route']]: isProjectRoute,
                    [styles['content-overlay-route']]: shouldUseOverlaySidebar
                }),
                children: /*#__PURE__*/ _jsx(Outlet, {
                    context: outletContext
                })
            }),
            /*#__PURE__*/ _jsx(SettingsModal, {}),
            /*#__PURE__*/ _jsx("button", {
                type: "button",
                "aria-label": intl.get('workspace.sidebar.close'),
                className: classNames(styles['workspace-overlay-mask'], {
                    [styles['workspace-overlay-mask-visible']]: shouldShowSidebarMask
                }),
                onClick: closeWorkspaceSidebar
            })
        ]
    });
}
